import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TradesmanLogo } from "./TradesmanLogo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Briefcase, Calendar, CheckCircle, DollarSign, Clock, MapPin, Star, MessageCircle, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { TradesmanRatingsDialog } from "./TradesmanRatingsDialog";
import { TradesmanPortfolioDialog } from "./TradesmanPortfolioDialog";
import { useIsMobile } from "@/hooks/use-mobile";

interface SimpleJobResponsesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  onJobUpdated: () => void;
  onOpenChat?: (jobId: string, tradesmanId: string) => void;
}

export const SimpleJobResponsesDialog = ({ open, onOpenChange, jobId, onJobUpdated, onOpenChat }: SimpleJobResponsesDialogProps) => {
  const [responses, setResponses] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [selectedTradesman, setSelectedTradesman] = useState<any>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open && jobId) {
      fetchResponses();
      fetchMessages();
    }
  }, [open, jobId]);

  const fetchResponses = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching responses for job:', jobId);
      
      // First get the basic job responses
      const { data: responseData, error: responseError } = await supabase
        .from("job_responses")
        .select(`
          id,
          message,
          estimated_cost,
          estimated_duration,
          availability,
          created_at,
          tradesman_id
        `)
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (responseError) {
        console.error('Error fetching responses:', responseError);
        throw responseError;
      }

      // For each response, fetch the tradesman details using our secure function
      const responsesWithTradesmen = await Promise.all(
        (responseData || []).map(async (response) => {
          try {
            const { data: tradesmanData, error: tradesmanError } = await supabase
              .rpc('get_tradesman_details_for_response', { response_id: response.id });

            if (tradesmanError) {
              console.error('Error fetching tradesman for response:', response.id, tradesmanError);
              return { ...response, tradesman: null };
            }

            return {
              ...response,
              tradesman: tradesmanData && tradesmanData.length > 0 ? tradesmanData[0] : null
            };
          } catch (error) {
            console.error('Error processing response:', response.id, error);
            return { ...response, tradesman: null };
          }
        })
      );

      console.log('Enhanced responses with tradesman data:', responsesWithTradesmen);
      setResponses(responsesWithTradesmen);
      
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load responses: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!messages_sender_id_fkey(
            id,
            full_name,
            company_name,
            user_type
          )
        `)
        .eq("job_id", jobId)
        .eq("is_blocked", false)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
        return;
      }

      const tradesmanMessages = (data || []).filter((m: any) => m.sender?.user_type === 'tradesman');
      const uniqueBySender = new Map<string, any>();
      for (const m of tradesmanMessages) {
        const sid = m.sender?.id;
        if (!sid) continue;
        if (!uniqueBySender.has(sid)) uniqueBySender.set(sid, m);
      }
      setMessages(Array.from(uniqueBySender.values()));
    } catch (e) {
      console.error('Messages fetch error:', e);
      setMessages([]);
    }
  };

  const handleAcceptQuote = async (response: any) => {
    try {
      console.log('Accepting quote:', response);
      
      // Include service fee (3%) in the agreed cost
      const totalWithFee = response.estimated_cost * 1.03;
      
      const { error } = await supabase
        .from("jobs")
        .update({ 
          tradesman_id: response.tradesman.id,
          agreed_cost: totalWithFee,
          status: "quote_accepted"
        })
        .eq("id", jobId);

      if (error) throw error;

      toast({
        title: "Quote accepted!",
        description: "Proceed to payment to confirm the tradesman assignment.",
      });

      onJobUpdated();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Accept quote error:', error);
      toast({
        title: "Error",
        description: "Failed to accept quote: " + error.message,
        variant: "destructive",
      });
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw] h-[90vh] max-h-[90vh]' : 'w-[98vw] max-w-6xl h-[95vh] max-h-[95vh]'} overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50 p-0 m-2 border-0 shadow-2xl`}>
        <DialogHeader className="border-b border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg`}>
              <Award className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-white`} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-foreground`}>
                Professional Quotes ({responses.length})
              </DialogTitle>
              <DialogDescription className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
                Select a tradesman to proceed with your job
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-200 border-t-blue-500"></div>
              <p className="mt-4 text-muted-foreground font-medium">Loading quotes...</p>
            </div>
          ) : (responses.length === 0 && messages.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mb-6 shadow-lg`}>
                <MessageCircle className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-blue-600`} />
              </div>
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-foreground mb-3`}>Awaiting Professional Quotes</h3>
              <p className={`${isMobile ? 'text-sm' : 'text-base'} text-muted-foreground text-center max-w-sm px-4`}>
                Qualified professionals will submit quotes quickly after your job posting.
              </p>
            </div>
          ) : (
            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {responses.map((response, index) => (
                <Card key={response.id} className="bg-gradient-blue text-white shadow-elegant hover:shadow-glow transition-all duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <TradesmanLogo 
                            logoUrl={response.tradesman?.logo_url}
                            companyName={response.tradesman?.company_name || response.tradesman?.full_name}
                            size="lg"
                            className="flex-shrink-0"
                          />
                          <CardTitle 
                            className={`${isMobile ? 'text-base' : 'text-lg sm:text-xl'} text-black font-bold truncate cursor-pointer hover:underline`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTradesman(response.tradesman);
                              setShowRatingsDialog(true);
                            }}
                          >
                            {response.tradesman?.company_name || response.tradesman?.full_name || 'Professional'}
                          </CardTitle>
                        </div>
                        <div className="mt-1">
                          <Badge variant="outline" className="text-xs bg-white text-black border-gray-300">
                            Quote ID: {response.id.substring(0, 8)}
                          </Badge>
                        </div>
                        <div className="mt-2 flex items-center gap-1 text-black font-medium">
                          <User className="h-4 w-4 flex-shrink-0" />
                          <span 
                            className="truncate cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTradesman(response.tradesman);
                              setShowRatingsDialog(true);
                            }}
                          >
                            {response.tradesman?.full_name || 'Professional Tradesman'}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Badge className="bg-green-600 text-white hover:bg-green-700 text-xs">
                          Available
                        </Badge>
                        {response.tradesman?.average_rating && (
                          <Badge 
                            variant="outline" 
                            className="text-xs bg-white text-black border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTradesman(response.tradesman);
                              setShowRatingsDialog(true);
                            }}
                          >
                            ⭐ {response.tradesman.average_rating.toFixed(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className={`${isMobile ? 'text-sm' : 'text-lg'} text-black font-medium mb-4 line-clamp-3`}>{response.message}</p>
                    
                    <div className={`space-y-2 ${isMobile ? 'text-sm' : 'text-base'} text-black font-medium mb-4`}>
                      {response.estimated_cost && (
                        <div className="flex items-center gap-2 text-green-600 font-bold">
                          <DollarSign className="h-4 w-4 flex-shrink-0" />
                          <span>Total Cost: £{Math.ceil(response.estimated_cost * 1.03)}</span>
                        </div>
                      )}
                      {response.estimated_duration && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">Duration: {response.estimated_duration}</span>
                        </div>
                      )}
                      {response.availability && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span>Available: {format(new Date(response.availability), "MMM d, yyyy")}</span>
                        </div>
                      )}
                    </div>

                    {/* Trade Specialties */}
                    {response.tradesman?.trade_specialties && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {response.tradesman.trade_specialties.slice(0, isMobile ? 2 : 3).map((specialty: string) => (
                            <Badge key={specialty} variant="outline" className="text-xs bg-white text-black border-gray-300">
                              {specialty.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptQuote(response);
                        }}
                        variant="hero" 
                        size={isMobile ? "sm" : "default"}
                        className="w-full shadow-glow hover:shadow-elegant transition-all duration-300"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Select This Tradesman
                      </Button>
                      
                      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTradesman(response.tradesman);
                            setShowRatingsDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="bg-white text-black border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <Star className="h-4 w-4 mr-1" />
                          {isMobile ? 'Reviews' : 'Reviews'}
                        </Button>
                        
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTradesman(response.tradesman);
                            setShowPortfolioDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="bg-white text-black border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <Briefcase className="h-4 w-4 mr-1" />
                          {isMobile ? 'Work' : 'Portfolio'}
                        </Button>

                        {onOpenChat && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenChat(jobId, response.tradesman?.id);
                            }}
                            variant="outline"
                            size="sm"
                            className={`bg-white text-black border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200 ${isMobile ? 'col-span-2' : ''}`}
                          >
                            <MessageCircle className="h-4 w-4 mr-1" />
                            Chat
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Dialogs */}
        {showRatingsDialog && selectedTradesman && (
          <TradesmanRatingsDialog
            open={showRatingsDialog}
            onOpenChange={setShowRatingsDialog}
            tradesmanId={selectedTradesman.id}
          />
        )}

        {showPortfolioDialog && selectedTradesman && (
          <TradesmanPortfolioDialog
            open={showPortfolioDialog}
            onOpenChange={setShowPortfolioDialog}
            tradesmanId={selectedTradesman.id}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};